/**
 * DPlayer Doc:
 * http://dplayer.js.org/#/zh-Hans/?id=hls
 */
import Hls from 'hls.js';
import Dplayer from 'dplayer';
import '@/utils/player/video/dplayer.less';
import { Dialog } from '@ymba/ym-ui';
import axios from 'axios';
import EventHandler from '@/utils/event';
import { MEDIA_ERROR_CODE } from '@/utils/constant';
import {
  storagePlayedTimeRange,
  getStoragePlayedTimeRange,
  removeStoragePlayTimeRange,
  storageStartPlayTime,
  getStorageStartPlayTime,
  removeStorageStartPlayTime,
  durationToTime,
  storagePlayTimeDuration,
} from '@/utils/index';
import Monitor from '@/common/monitor';


if (typeof window !== 'undefined') {
  window.Hls = Hls;
}

class YmDplayer {
  constructor(options) {
    // 设置播放器
    this.player = null;

    // 事件处理器
    this.eventHandler = new EventHandler();
    // 事件列表
    this.events = [
      'abort',
      'canplay',
      'canplaythrough',
      'durationchange',
      'emptied',
      'ended',
      'error',
      'loadeddata',
      'loadedmetadata',
      'loadstart',
      'mozaudioavailable',
      'pause',
      'play',
      'playing',
      'progress',
      'ratechange',
      'seeked',
      'seeking',
      'stalled',
      'suspend',
      'timeupdate',
      'volumechange',
      'waiting',
      'screenshot',
      'thumbnails_show',
      'thumbnails_hide',
      'danmaku_show',
      'danmaku_hide',
      'danmaku_clear',
      'danmaku_loaded',
      'danmaku_send',
      'danmaku_opacity',
      'contextmenu_show',
      'contextmenu_hide',
      'notice_show',
      'notice_hide',
      'quality_start',
      'quality_end',
      'destroy',
      'resize',
      'fullscreen',
      'fullscreen_cancel',
      'subtitle_show',
      'subtitle_hide',
      'subtitle_change',
    ];

    // log处理器
    const { logHandler } = options;
    this.logHandler = logHandler;

    // 播放ID
    this.playingId = 0;

    // 暂停ID
    this.pauseId = 0;

    // 结束ID
    this.endedId = 0;

    // 当前资源对象
    this.sourceObject = null;

    // 获取环境
    this.env = process.env.NODE_ENV;

    // sentry 打log
    if (this.env === 'production') {
      this.sentry = Monitor.getInstance({
        environment: process.env.VUE_APP_PACK_MODE,
      });
    }
  }

  /**
   * 设置播放ID
   * @param value
   */
  setPlayingId(value) {
    this.playingId = value;
  }

  /**
   * 设置暂停ID
   * @param value
   */
  setPauseId(value) {
    this.pauseId = value;
  }

  /**
   * 设置结束ID
   * @param value
   */
  setEndedId(value) {
    this.endedId = value;
  }

  /**
   * 设置资源对象
   * @param value
   */
  setSourceObject(value) {
    this.sourceObject = value;
  }

  /**
   * 获取播放时间
   * @returns {*}
   */
  getCurrentTime() {
    const { player } = this;
    if (player == null) return 0;
    return player.video.currentTime;
  }

  /**
   * 向sentry 打log
   * @param data
   * @param type
   * @param message
   */
  logSentry(data, type = 'error', message) {
    const { sentry } = this;
    if (sentry == null) return;
    this.sentry.log(data, type, message);
  }

  /**
   * 初始化播放器
   * @param params
   */
  initPlayer(params) {
    const { player } = this;
    // 如果播放器已存在，则先销毁对象
    if (player != null) {
      player.destroy();
    }

    // 播放器默认选项值
    const defaultOptions = {
      theme: '#13b9bf',
      lang: 'zh-cn',
      preload: 'auto',
      mutex: true,
    };
    const options = Object.assign(defaultOptions, params);

    // 播放器
    this.player = new Dplayer(options);

    // add event listener
    this.addEventListener();
  }

  /**
   * 播放数打点
   */
  logPlayCount() {
    return new Promise(async (resolve, reject) => {
      // 获取当前资源
      const { sourceObject } = this;
      // 当前资源不存在则退出
      if (sourceObject == null) return;
      // 获取当前资源ID，类型
      const { id, type, audioId } = sourceObject;
      try {
        const data = await this.logHandler.logPlayCount(id, type, audioId);
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * 播放进度打Log
   */
  logPlayProgress() {
    return new Promise(async (resolve, reject) => {
      // 获取当前资源
      const { sourceObject } = this;
      // 当前资源不存在则退出
      if (sourceObject == null) return;
      // 获取当前资源ID，时长，类型
      const {
        id,
        duration: time,
        type,
        audioId,
      } = sourceObject;
      // 获取已缓存播放时间段
      const range = getStoragePlayedTimeRange(id, type);
      if (range.length > 0) {
        // 获取播放器时长
        const { player } = this;
        const { video } = player;
        let { duration } = video;
        if (!duration && Number.isNaN(duration)) {
          // 转换时长
          duration = durationToTime(time);
        }
        // 收集打点数据
        const params = {
          id,
          type,
          duration,
          range,
          audioId,
        };
        try {
          const data = await this.logHandler.logPlayProgress(id, params);
          removeStoragePlayTimeRange(id, type);
          resolve(data);
        } catch (e) {
          reject(e);
        }
      }
    });
  }

  /**
   * 缓存开始播放时间
   */
  storageStartTime() {
    // 获取当前资源
    const { sourceObject } = this;
    // 当前资源不存在则退出
    if (sourceObject == null) return;
    // 获取当前资源ID，时长
    const { id, type } = sourceObject;
    // 获取时间
    const time = this.getCurrentTime();
    // 记录播放开始时间
    storageStartPlayTime(id, type, time);
  }

  /**
   * 缓存已播时间段
   */
  async storageTimeRange() {
    // 获取当前资源
    const { sourceObject } = this;
    // 当前资源不存在则退出
    if (sourceObject == null) return;
    // 获取当前资源ID，时长
    const { id, type } = sourceObject;
    // 获取已缓存时间段
    const range = getStoragePlayedTimeRange(id, type);
    // 获取已缓存的开始播放时间
    const start = getStorageStartPlayTime(id, type);
    // 获取当前时间
    const end = this.getCurrentTime();
    if (end > 0 && end > start) {
      // 数据
      const data = {
        start,
        end,
      };
      // 增加时间段
      range.push(data);
      // 缓存至本地
      storagePlayedTimeRange(id, type, range);
      // 更新已播时间
      storageStartPlayTime(id, type, end);
    }
  }

  /**
   * 切换视频和弹幕
   * @param object
   * @param danmaku
   */
  switchVideo(object, danmaku) {
    const { player, sourceObject, endedId } = this;
    if (player == null) return;

    // 获取播放ID
    const { playingId } = this;
    // 获取新资源ID
    const {
      id,
      url,
      pic,
      videoType: type,
      duration,
      type: objectType,
    } = object;

    // 资源未更新，则return 函数
    if (playingId === id) return;

    const { video } = player;
    // 缓存其他音频已播时间
    if (!video.paused) {
      // 本地缓存上一首进度
      this.storageTimeRange();
    }

    if (sourceObject != null) {
      const { id: currentId } = sourceObject;
      if (currentId !== endedId) {
        // 上一首音频进行打点
        this.logPlayProgress();
      }
    }

    // 视频对象
    const videoObject = {
      url,
      pic,
      type,
    };

    // 切换视频和弹幕
    this.player.switchVideo(videoObject, danmaku);

    // 记录音频时长
    storagePlayTimeDuration(id, objectType, duration);

    // 设置当前资源对象
    this.setSourceObject(object);
  }

  play(video, danmaku) {
    const { player } = this;
    if (player == null) return;
    // 切换视频
    this.switchVideo(video, danmaku);
    // 进行播放
    player.play();
  }

  pause() {
    const { player } = this;
    if (player == null) return;
    player.pause();
  }

  rate(rate) {
    const { player } = this;
    if (player == null) return;
    player.speed(rate);
  }

  seek(time) {
    const { player } = this;
    if (player == null) return;
    // 调用播放器跳转
    player.seek(time);
  }

  volume(percentage, noStorage, noNotice) {
    const { player } = this;
    if (player == null) return;
    player.volume(percentage, noStorage, noNotice);
  }

  notice(text, time, opacity) {
    const { player } = this;
    if (player == null) return;
    player.notice(text, time, opacity);
  }

  switchQuality(index) {
    const { player } = this;
    if (player == null) return;
    player.switchQuality(index);
  }

  sendDanmaku(danmaku) {
    return new Promise((resolve) => {
      const { player } = this;
      if (player == null) return;
      player.danmaku.send(danmaku, () => resolve());
    });
  }

  drawDanmaku(danmaku) {
    const { player } = this;
    if (player == null) return;
    player.danmaku.draw(danmaku);
  }

  setDanmakuOpacity(opacity) {
    const { player } = this;
    if (player == null) return;
    player.danmaku.opacity(opacity);
  }

  clearDanmaku() {
    const { player } = this;
    if (player == null) return;
    player.danmaku.clear();
  }

  hideDanmaku() {
    const { player } = this;
    if (player == null) return;
    player.danmaku.hide();
  }

  showDanmaku() {
    const { player } = this;
    if (player == null) return;
    player.danmaku.show();
  }

  /**
   * 添加事件监控
   */
  addEventListener() {
    this.events.forEach((key) => {
      this.player.on(`${key}`, (e) => {
        const { sourceObject } = this;
        this.eventHandler.emit(`${key}`, e, sourceObject);
        const fn = this[`on${key}`];
        if (fn && typeof fn === 'function') {
          fn.call(this, e, sourceObject);
        }
      }, false);
    });
  }


  /**
   * 播放错误
   */
  async onerror(e = {}) {
    const { player } = this;
    if (player == null) {
      // 发送错误监控
      this.logSentry(new Error('player not exist'), 'error', 'VideoPlayError');
      return;
    }
    const { video } = player;

    // 重置播放ID
    this.setPlayingId(0);
    // 重置暂停ID
    this.setPauseId(0);

    // 获取错误信息
    const { error = {} } = video;
    const { code = 0 } = error || {};
    const { currentSrc = '' } = video;
    let responseError = '';
    let response = '';
    if (currentSrc !== '') {
      try {
        const res = await axios({
          url: currentSrc,
        });
        const { status, statusText, headers } = res;
        response = JSON.stringify({ status, statusText, headers });
      } catch (requestError) {
        responseError = requestError;
      }
    }

    // 解析错误原因
    const errorReason = MEDIA_ERROR_CODE[code] || {};
    const {
      name: MediaErrorName = '',
      message: MediaErrorMessage = '',
    } = errorReason;

    const { message: errorFunctionCatchMessage = '' } = e;
    const currentTime = this.getCurrentTime();

    // 获取当前资源对象
    const { sourceObject } = this;

    // 发送错误监控
    const errorLogParams = {
      sourceObject,
      currentSrc,
      currentTime,
      errorFunctionCatchMessage,
      response,
      responseError,
      MediaErrorName,
      MediaErrorMessage,
    };
    // 发送错误监控
    this.logSentry(errorLogParams, 'error', 'VideoPlayError');
    // 提示用户
    if (MediaErrorMessage !== '') {
      Dialog.alert({ message: '网络出现问题，请稍后再试' });
    }
  }

  /**
   * 播放被中断
   */
  onabort() {
    // 重置播放ID
    this.setPlayingId(0);
    // 重置暂停ID
    this.setPauseId(0);
    // 收集时间段
    this.storageTimeRange();
  }

  /**
   * 开始播放
   */
  onplay() {
    // 收集开始时间
    this.storageStartTime();
    // 获取暂停ID
    const { pauseId, sourceObject } = this;
    // 获取当前ID
    const { id } = sourceObject;
    if (pauseId !== id) {
      // 记录播放数
      this.logPlayCount();
    }
  }

  onseeking() {
    // 跳转前，收集时间段
    this.storageTimeRange();
  }

  /**
   * 跳转结束
   */
  onseeked() {
    // 收集开始时间
    this.storageStartTime();
  }

  /**
   * 播放暂停
   * @param e
   * @param object
   */
  onpause(e, object) {
    const { id } = object;
    // 设置暂停ID
    this.setPauseId(id);
    // 重置播放ID
    this.setPlayingId(0);
    // 收集时间段
    this.storageTimeRange();
  }

  /**
   * 播放结束
   * @param e
   * @param object
   */
  onended(e, object) {
    // 重置播放ID
    this.setPlayingId(0);
    // 重置暂停ID
    this.setPauseId(0);

    // 删除开始播放时间缓存
    const { id, type } = object;
    removeStorageStartPlayTime(id, type);

    // 收集播放进度
    this.logPlayProgress().then((data) => {
      // 触发本章学习完成事件
      this.eventHandler.emit('lessonComplete', {
        id,
        data,
      });
    });

    // 设置结束ID
    this.setEndedId(id);
  }

  /**
   * 事件监控
   * @param type
   * @param fn
   */
  on(type, fn) {
    return this.eventHandler.on(type, fn);
  }

  /**
   * 只监控事件一次
   * @param type
   * @param fn
   * @returns {*}
   */
  once(type, fn) {
    return this.eventHandler.once(type, fn);
  }

  /**
   * 取消事件监控
   * @param type
   * @param fn
   * @returns {undefined}
   */
  off(type, fn) {
    return this.eventHandler.off(type, fn);
  }
}

export default YmDplayer;
